import { Show, type Component, createMemo } from 'solid-js'
import { useLocation } from '@solidjs/router'
import { AppRoutes } from '../../shared/app-routes'
import { Trans } from '@mbarzda/solid-i18next'
import UserMenu from './UserMenu'
import Breadcrumbs, { type BreadcrumbSegment } from './Breadcrumbs'

interface TopbarProps {
  onSidebarOpen?: () => void
  hidden?: boolean
}

/** Simple page titles — no breadcrumb trail, just a single label via i18n key. */
const PAGE_TITLES: Record<string, string> = {
  [AppRoutes.Dashboard()]: 'dashboard',
  [AppRoutes.UsersOverview()]: 'users_overview',
  [AppRoutes.CashFlow()]: 'cash_flow',
  [AppRoutes.ChurnOverview()]: 'churn_overview',
  [AppRoutes.Sections()]: 'sections',
  [AppRoutes.Songs()]: 'songs',
  [AppRoutes.Videos()]: 'videos',
  [AppRoutes.Events()]: 'events',
  [AppRoutes.Community()]: 'community',
  [AppRoutes.Chat()]: 'chat',
  [AppRoutes.EngagementUsers()]: 'users',
  [AppRoutes.UserDirectory()]: 'user_directory',
  [AppRoutes.Reports()]: 'reports',
  [AppRoutes.Moderation()]: 'moderation',
  [AppRoutes.KeywordFlagging()]: 'keyword_flagging',
  [AppRoutes.Polls()]: 'polls',
  [AppRoutes.AdminEvents()]: 'admin_events'
}

/**
 * Resolve the current pathname into breadcrumb segments.
 * Returns null for simple pages (handled by PAGE_TITLES).
 */
const resolveBreadcrumbs = (pathname: string): BreadcrumbSegment[] | null => {
  // Engagement sub-routes: Users › Top Song Listeners
  if ([AppRoutes.TopSongListeners(), AppRoutes.TopForumPosters(), AppRoutes.TopChatMessengers()].includes(pathname)) {
    const labels: Record<string, string> = {
      [AppRoutes.TopSongListeners()]: 'Top Song Listeners',
      [AppRoutes.TopForumPosters()]: 'Top Forum Posters',
      [AppRoutes.TopChatMessengers()]: 'Top Chat Messengers'
    }
    return [{ label: 'Users', href: AppRoutes.EngagementUsers() }, { label: labels[pathname] ?? 'Details' }]
  }

  // Keyword flagging sub-routes
  if (pathname === AppRoutes.KeywordFlaggingNew()) {
    return [{ label: 'Keyword Flagging', href: AppRoutes.KeywordFlagging() }, { label: 'Add New' }]
  }
  if (pathname.startsWith('/keyword-flagging/edit/')) {
    return [{ label: 'Keyword Flagging', href: AppRoutes.KeywordFlagging() }, { label: 'Edit' }]
  }

  // Polls sub-routes
  if (pathname === AppRoutes.PollsNew()) {
    return [{ label: 'Polls', href: AppRoutes.Polls() }, { label: 'New poll' }]
  }
  if (pathname.startsWith('/polls/edit/')) {
    return [{ label: 'Polls', href: AppRoutes.Polls() }, { label: 'Edit poll' }]
  }

  // Admin events sub-routes
  if (pathname === AppRoutes.AdminEventsCreate()) {
    return [{ label: 'Events', href: AppRoutes.AdminEvents() }, { label: 'Create an event' }]
  }
  if (pathname === AppRoutes.AdminPresalePoolCreate()) {
    return [
      { label: 'Events', href: AppRoutes.AdminEvents() },
      { label: 'Presale Unique Codes', href: AppRoutes.AdminPresalePoolCreate() },
      { label: 'Create' }
    ]
  }
  if (pathname === AppRoutes.AdminPresalePoolEdit()) {
    return [{ label: 'Events', href: AppRoutes.AdminEvents() }, { label: 'Presale Unique Codes' }, { label: 'Edit' }]
  }
  if (pathname.startsWith('/admin/events/') && pathname.endsWith('/edit')) {
    return [{ label: 'Events', href: AppRoutes.AdminEvents() }, { label: 'Edit' }]
  }

  return null
}

const Topbar: Component<TopbarProps> = (props) => {
  const location = useLocation()

  const breadcrumbs = createMemo(() => resolveBreadcrumbs(location.pathname))

  const simpleTitle = createMemo(() => {
    if (breadcrumbs()) return null
    return PAGE_TITLES[location.pathname] ?? 'dashboard'
  })

  return (
    <div class="bg-gray-50 top-0 z-40 flex h-16 shrink-0 items-center px-6">
      <div class="px-4 md:px-0 flex flex-1 items-center gap-x-4 sm:gap-x-6 max-w-[1280px] mx-auto w-full">
        <Show when={!!props.onSidebarOpen}>
          <button onClick={props.onSidebarOpen} type="button" class="-m-2.5 p-2.5 text-gray-700 lg:hidden">
            <span class="sr-only">Open sidebar</span>
            <svg
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <div class="h-6 w-px bg-gray-900/10 hidden sm:block lg:hidden" aria-hidden="true" />
        </Show>

        <div class="flex flex-1 gap-x-1 sm:gap-x-6 self-stretch lg:gap-x-10">
          <div class="relative flex flex-1 items-center gap-x-2">
            <Show
              when={breadcrumbs()}
              fallback={
                <h1 class="text-xl font-medium text-gray-700">
                  <Trans key={`ls_sidebar_${simpleTitle()}`} />
                </h1>
              }
            >
              <Breadcrumbs segments={breadcrumbs()!} />
            </Show>
          </div>

          <div class="flex items-center gap-x-4 lg:gap-x-6">
            <UserMenu />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Topbar
